import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DealerNav from "./DealerNav";

function getRoleFromToken() {
  const t = localStorage.getItem("dealerToken");
  if (!t) return null;
  const parts = t.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return (
      payload.role ||
      payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ||
      null
    );
  } catch {
    return null;
  }
}

// NEW: get current user's email from token (for self-revoke guard)
function getEmailFromToken() {
  try {
    const t = localStorage.getItem("dealerToken");
    if (!t) return null;
    const payload = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.email ?? payload.upn ?? payload.unique_name ?? null;
  } catch {
    return null;
  }
}

// Normalize API base so we never hit /api/api
const RAW = process.env.REACT_APP_API_BASE || "";
const API_BASE = RAW.replace(/\/+$/, "");
const APIv1 = API_BASE.endsWith("/api") ? API_BASE : `${API_BASE}/api`;

// --- tiny inline chevrons (no deps)
function Chevron({ dir }) {
  return (
    <svg
      className="inline-block"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      {dir === "asc" ? (
        <path d="M7 14l5-5 5 5" fill="none" stroke="currentColor" strokeWidth="2" />
      ) : (
        <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
      )}
    </svg>
  );
}

export default function DealerStaff() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("dealerToken") || "", []);
  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : {}),
    [token]
  );

  const role = getRoleFromToken();
  const isAdmin = role === "DealerAdmin";
  const selfEmail = getEmailFromToken(); // NEW

  // redirect if not logged in
  useEffect(() => {
    if (!token) navigate("/dealer/login", { replace: true });
  }, [token, navigate]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // 🔽 local status filter
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | revoked

  // 🔽 sorting (default: Last Login DESC)
  const [sortField, setSortField] = useState("lastLoginAt");
  const [sortDir, setSortDir] = useState("desc");

  const sortAccess = {
    email: (u) => (u.email || "").toString(),
    fullName: (u) => (u.fullName || "").toString(),
    role: (u) => (u.role || "").toString(),
    emailVerified: (u) => (u.emailVerified ? 1 : 0),
    isActive: (u) => (u.isActive ? 1 : 0),
    lastLoginAt: (u) => (u.lastLoginAt ? new Date(u.lastLoginAt).getTime() : 0),
  };

  function applySort(list) {
    const accessor = sortAccess[sortField] || ((x) => x);
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }

  function toggleSort(field) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  const filteredRows = rows.filter((u) => {
    if (statusFilter === "active") return !!u.isActive;
    if (statusFilter === "revoked") return !u.isActive;
    return true;
  });

  const displayRows = applySort(filteredRows);

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setErr("");
      const r = await fetch(`${APIv1}/dealer/staff/users`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.items || []);
    } catch (e) {
      setErr("Failed to load dealer staff.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UPDATED: confirm dialogs
  const revoke = async (id, email) => {
    if (!window.confirm(`Revoke access for ${email || "this user"}?`)) return;
    await fetch(`${APIv1}/dealer/staff/users/${id}/revoke`, { method: "POST", headers });
    load();
  };

  const reactivate = async (id, email) => {
    if (!window.confirm(`Reactivate ${email || "this user"}?`)) return;
    await fetch(`${APIv1}/dealer/staff/users/${id}/reactivate`, { method: "POST", headers });
    load();
  };

  // --- small, text-like sortable TH
  function SortableTH({ field, children, className = "" }) {
    const active = sortField === field;
    const ariaSort = active ? (sortDir === "asc" ? "ascending" : "descending") : "none";
    return (
      <th
        scope="col"
        aria-sort={ariaSort}
        onClick={() => toggleSort(field)}
        className={`p-3 text-left text-sm font-medium select-none cursor-pointer hover:text-blue-600 ${className}`}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {active && <Chevron dir={sortDir} />}
        </span>
      </th>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Dealer navigation always at top */}
      <DealerNav />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-semibold">Dealer Staff</h1>

        {/* Status filter + Invite */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 rounded-lg border border-gray-300 text-sm bg-white"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
          </select>

          <button
            onClick={() => navigate("/dealer/invites")}
            className="h-10 px-4 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap"
          >
            + Invite Staff
          </button>
        </div>
      </div>

      {/* Error */}
      {err && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <SortableTH field="email">Email</SortableTH>
              <SortableTH field="fullName">Name</SortableTH>
              <SortableTH field="role">Role</SortableTH>
              <SortableTH field="emailVerified">Email Verified</SortableTH>
              <SortableTH field="isActive">Status</SortableTH>
              <SortableTH field="lastLoginAt">Last Login</SortableTH>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="[&>tr:nth-child(odd)]:bg-white [&>tr:nth-child(even)]:bg-gray-50">
            {loading ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-500">
                  No staff yet
                </td>
              </tr>
            ) : (
              displayRows.map((u) => {
                const isSelf =
                  !!(u.email && selfEmail && u.email.toLowerCase() === selfEmail.toLowerCase());
                return (
                  <tr key={u.id} className="border-t">
                    <td className="p-3">{u.email}</td>
                    <td className="p-3">{u.fullName || "—"}</td>
                    <td className="p-3">{u.role || "—"}</td>
                    <td className="p-3">{u.emailVerified ? "Yes" : "No"}</td>
                    <td className="p-3">{u.isActive ? "Active" : "Revoked"}</td>
                    <td className="p-3">
                      {u.lastLoginAt
                        ? new Date(u.lastLoginAt).toLocaleString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                    <td className="p-3">
                      {isAdmin ? (
                        <div className="flex justify-end gap-2">
                          {u.isActive ? (
                            <button
                              onClick={() => {
                                if (isSelf) return; // block self-revoke
                                revoke(u.id, u.email);
                              }}
                              disabled={isSelf}
                              title={isSelf ? "You can't revoke yourself" : "Revoke"}
                              className="h-9 px-3 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                            >
                              Revoke
                            </button>
                          ) : (
                            <button
                              onClick={() => reactivate(u.id, u.email)}
                              title="Reactivate"
                              className="h-9 px-3 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700"
                            >
                              Reactivate
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">View only</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
